/**
 * MisSubastas — Panel del vendedor: borradores sin pagar + subastas por estado.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useCountdown } from "@/components/AuctionCard";
import { Badge, CardPhoto, Label, money } from "@/components/auction/AuctionUI";
import { OrderAction, OrderStageBadge, OrderSteps } from "@/components/auction/OrderStage";
import { MisPujas } from "@/components/auction/MisPujas";
import { TermsLink } from "@/components/auction/AuctionTerms";
import { Plus, Mail, AlertTriangle, Gavel } from "lucide-react";
import { HitSectionLoading, useLoadingLevel } from "@/components/loading";

function AuctionEmailPrefs() {
  const [p, setP] = useState(null);
  useEffect(() => { api.get("/me/auction-email-prefs").then((r) => setP(r.data)).catch(() => {}); }, []);
  if (!p) return null;
  const toggle = async (k) => {
    const next = { ...p, [k]: !p[k] };
    setP(next);
    try { await api.put("/me/auction-email-prefs", next); toast.success("Preferencias guardadas"); }
    catch { toast.error("No se pudo guardar"); setP(p); }
  };
  return (
    <section className="sx-card p-4 mt-8" data-testid="auction-email-prefs">
      <div className="sx-mono text-[10px] tracking-[.14em] text-[#FF4438] mb-2 flex items-center gap-2"><Mail size={13} /> AVISOS POR CORREO</div>
      {[["new_auctions", "Nuevas subastas"], ["outbid", "Cuando alguien supera mi puja"], ["closing_soon", "Cuando una subasta en la que pujé está por cerrar"]].map(([k, label]) => (
        <label key={k} className="flex items-center justify-between gap-3 py-2 text-sm text-white/85">
          <span>{label}</span>
          <Switch checked={!!p[k]} onCheckedChange={() => toggle(k)} data-testid={`auction-pref-${k}`} className="data-[state=checked]:bg-[#FF4438]" />
        </label>
      ))}
      <p className="text-[11px] text-white/45 mt-1">Los correos de ganaste, recordatorio de pago, repechaje y resultado de tu subasta siempre se envían.</p>
    </section>
  );
}

const STATUS = {
  active: { label: "● Activa", tone: "green" },
  pending_review: { label: "⌛ En revisión", tone: "amber" },
  rejected: { label: "✕ Rechazada", tone: "coral" },
  finalized: { label: "Finalizada", tone: "gray" },
};
const FILTERS = [["all", "Todas"], ["active", "Activas"], ["pending_review", "En revisión"], ["finalized", "Finalizadas"], ["rejected", "Rechazadas"]];
const DRAFT_TTL_MS = 48 * 3600 * 1000;

function draftLeft(createdAt) {
  const ms = new Date(createdAt).getTime() + DRAFT_TTL_MS - Date.now();
  if (ms <= 60000) return { text: "menos de 1 minuto", urgent: true };
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return { text: h > 0 ? `${h}h ${String(m).padStart(2, "0")}m` : `${m}m`, urgent: ms < 6 * 3600000 };
}

const RESULT = { sold: "Vendida", awaiting_payment: "Esperando pago del ganador", repechage_offered: "En repechaje", processing_default: "Procesando repechaje", unsold: "Cerrada sin venta", no_bids: "Terminó sin pujas" };

function Detail({ a }) {
  const c = useCountdown(a.status === "active" ? a.ends_at : null);
  if (a.status === "active") return a.current_bid != null
    ? <span className="text-[#34D399]">Puja actual {money(a.current_bid)} · cierra en {(c || "").replace(/:\d\d$/, "")}</span>
    : <span className="text-[#34D399]">Sin pujas aún · cierra en {(c || "").replace(/:\d\d$/, "")}</span>;
  if (a.status === "pending_review") return a.transfer_verification_pending
    ? <span className="text-amber-400">Transferencia pendiente de verificar</span>
    : <span className="text-white/55">Revisión del administrador · menos de 24 h</span>;
  if (a.status === "rejected") return <span className="text-white/55">Puedes corregir y volver a enviarla</span>;
  if (a.status === "finalized") {
    const r = RESULT[a.result] || "Finalizada";
    return <span className="text-white/55">{r}{a.final_price != null && ["sold", "awaiting_payment", "repechage_offered"].includes(a.result) ? ` · ${money(a.final_price)}` : ""}</span>;
  }
  return null;
}

function DraftCard({ d }) {
  const t = draftLeft(d.created_at);
  return (
    <div data-testid={`draft-row-${d.draft_token}`} className="rounded-[22px] border border-amber-500/45 p-4 lg:px-5 lg:py-4 flex flex-col lg:flex-row lg:items-center gap-4"
         style={{ background: "linear-gradient(160deg, rgba(245,158,11,.10), #0f0d09 60%)" }}>
      <div className="flex items-center gap-3.5 flex-1 min-w-0">
        <CardPhoto path={d.photos?.[0]} alt={d.product_name} w={46} h={62} size={200} rotate={0} glow="amber" />
        <div className="min-w-0">
          <span className="hidden lg:inline sx-mono text-[10px] tracking-[.14em] text-amber-400 mr-3">BORRADOR</span>
          <span className="text-lg font-extrabold">{d.product_name}</span>
          <div className="text-xs text-white/55 mt-0.5">{d.duration_days} día(s) · A pagar <b className="text-white">{money(d.day_fee_owed)}</b> · Creado {new Date(d.created_at).toLocaleDateString("es-EC")}</div>
        </div>
      </div>
      <div className="flex items-baseline gap-2" data-testid={`draft-expires-${d.draft_token}`}>
        <span className={`sx-mono text-[22px] ${t.urgent ? "text-[#FF4438]" : "text-amber-400"}`}>{t.text}</span>
        <span className="text-xs text-white/55">para pagar · luego se borra solo</span>
      </div>
      <div className="flex gap-2">
        <Link to={`/subastas/crear?draft=${d.draft_token}`} data-testid={`draft-edit-${d.draft_token}`} className="sx-btn-ghost flex-1 lg:flex-none h-12 px-6 flex items-center justify-center">Editar</Link>
        <Link to={`/subastas/pago/${d.draft_token}`} data-testid={`draft-pay-${d.draft_token}`} className="sx-btn flex-1 lg:flex-none h-12 px-6 flex items-center justify-center">Pagar {money(d.day_fee_owed)}</Link>
      </div>
    </div>
  );
}

function AuctionRow({ a }) {
  const s = STATUS[a.status] || { label: a.status, tone: "gray" };
  const rejected = a.status === "rejected";
  return (
    <div data-testid={`auction-row-${a.id}`} className={`rounded-[20px] border p-3.5 lg:rounded-none lg:border-0 lg:border-b lg:border-white/8 lg:px-5 ${rejected ? "border-[#FF4438]/45 bg-[#FF4438]/[.03]" : "border-white/12 bg-[#101010] lg:bg-transparent"}`}>
      <div className="flex items-center gap-3.5 lg:grid lg:grid-cols-[2.2fr_1fr_1fr_1.2fr_2fr]">
        <div className="flex items-center gap-3.5 flex-1 min-w-0">
          <CardPhoto path={a.photos?.[0]} alt={a.product_name} w={42} h={56} size={200} rotate={0} glow="gray" />
          <div className="min-w-0">
            <div className="font-extrabold leading-tight">{a.product_name}</div>
            <div className="text-xs text-white/55 mt-0.5 lg:hidden">{a.duration_days}d · {money(a.starting_price)} salida</div>
            <div className="text-xs mt-0.5 lg:hidden"><Detail a={a} /></div>
          </div>
        </div>
        <span className="hidden lg:block text-sm text-white/70">{a.duration_days} día{a.duration_days > 1 ? "s" : ""}</span>
        <span className="hidden lg:block sx-mono text-sm text-white/70">{money(a.starting_price)}</span>
        <span className="flex-none"><Badge tone={s.tone}>{s.label}</Badge></span>
        <span className="hidden lg:block text-xs">{rejected && a.reject_reason ? <span className="text-white/60">Motivo: {a.reject_reason}</span> : <Detail a={a} />}</span>
      </div>
      {rejected && a.reject_reason && (
        <div className="lg:hidden mt-3 rounded-[14px] border border-[#FF4438]/40 bg-[#FF4438]/[.08] p-3 text-[13px] text-white/75" data-testid={`auction-reject-${a.id}`}>
          <b className="text-[#ff6b61]">Motivo:</b> {a.reject_reason}
        </div>
      )}
      {rejected && (
        <div className="mt-3 lg:ml-[56px] flex flex-col sm:flex-row sm:items-center gap-2.5" data-testid={`auction-resubmit-box-${a.id}`}>
          <Link to={`/subastas/crear?resubmit=${a.id}`} data-testid={`auction-resubmit-${a.id}`} className="sx-btn h-10 px-5 inline-flex items-center justify-center text-sm whitespace-nowrap">Corregir y reenviar · sin pagar de nuevo</Link>
          {Number(a.day_fee_owed) > 0 && <span className="text-[12px] text-white/55">La tarifa por días que pagaste queda a favor de esta subasta.</span>}
        </div>
      )}
      {a.order && (
        <div className="mt-3 lg:ml-[56px] rounded-[14px] border border-white/10 bg-white/[.02] p-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`auction-order-${a.id}`}>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="sx-mono text-[10px] tracking-[.14em] text-white/50">PEDIDO</span>
              <OrderStageBadge order={a.order} testid={`auction-order-stage-${a.id}`} />
              <span className="text-xs text-white/55">Total {money(a.order.total)}</span>
            </div>
            <OrderSteps status={a.order.status} testid={`auction-order-steps-${a.id}`} />
          </div>
          <OrderAction order={a.order} auctionId={a.id} role="seller" />
        </div>
      )}
      {["active", "finalized"].includes(a.status) && (
        <Link to={`/subastas/${a.id}`} className="inline-block mt-2 lg:mt-1 lg:ml-[56px] text-[13px] font-bold text-[#FF4438] hover:underline" data-testid={`auction-link-${a.id}`}>Ver subasta pública →</Link>
      )}
    </div>
  );
}

export default function MisSubastas() {
  const [drafts, setDrafts] = useState([]);
  const [auctions, setAuctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") === "pujas" ? "pujas" : "vendo");
  const level = useLoadingLevel(loading);
  const busy = loading || level !== "none";

  useEffect(() => {
    const pago = new URLSearchParams(window.location.search).get("pago");
    if (pago === "approved") toast.success("Pago aprobado. Tu subasta pasó a revisión del administrador.");
    else if (pago) toast.error("El pago con tarjeta no se completó. Tu borrador sigue disponible para intentarlo de nuevo.");
  }, []);

  useEffect(() => {
    Promise.all([
      api.get("/auctions/drafts/mine").catch(() => ({ data: { results: [] } })),
      api.get("/auctions/mine").catch(() => ({ data: { results: [] } })),
    ]).then(([d, a]) => {
      setDrafts(d.data.results || []);
      setAuctions(a.data.results || []);
    }).finally(() => setLoading(false));
  }, []);

  const count = (k) => auctions.filter((a) => a.status === k).length;
  const shown = filter === "all" ? auctions : auctions.filter((a) => a.status === filter);

  return (
    <div className="sx relative overflow-hidden">
      <div className="sx-halo" />
      <div className="relative max-w-[1180px] mx-auto px-5 lg:px-10 pt-8 pb-16">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
          <div>
            <h1 className="sx-h text-[46px] lg:text-[72px]">Mis <span className="sx-it">subastas</span></h1>
            <p className="text-[15px] text-white/65 mt-2">Lo que vendes y lo que ganas: pagos, envíos, entregas y liberación.</p>
            <TermsLink testid="mis-terms-link" className="mt-2" />
          </div>
          <Link to="/subastas/crear" data-testid="mis-link-crear" className="sx-btn lg:order-last h-[52px] px-7 flex items-center justify-center gap-1.5 lg:hidden"><Plus size={16} /> Nueva subasta</Link>
          <Link to="/subastas/crear" data-testid="mis-link-crear-desktop" className="sx-btn hidden lg:flex h-[52px] px-7 items-center justify-center gap-1.5"><Plus size={16} /> Nueva subasta</Link>
        </div>

        <div className="flex gap-1 mt-6 p-1 rounded-full border border-white/12 w-full sm:w-fit" data-testid="mis-tabs">
          {[["vendo", "Vendo"], ["pujas", "Gano y pujo"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setTab(k)} data-testid={`mis-tab-${k}`}
                    className={`flex-1 sm:flex-none h-10 px-6 rounded-full text-sm font-bold transition-colors ${tab === k ? "bg-[#FF4438] text-white" : "text-white/70 hover:text-white"}`}>{l}</button>
          ))}
        </div>

        {tab === "pujas" && <MisPujas />}

        {tab === "vendo" && <>
        <div className="grid grid-cols-4 gap-2 mt-5 lg:hidden" data-testid="mis-summary">
          {[["active", "Activas", "#fff"], ["pending_review", "Revisión", "#fbbf24"], ["finalized", "Finaliz.", "rgba(255,255,255,.55)"], ["rejected", "Rechaz.", "#ff6b61"]].map(([k, l, c]) => (
            <div key={k} className="sx-card rounded-[14px] py-2.5 text-center" data-testid={`mis-count-${k}`}>
              <div className="sx-price text-[22px]">{count(k)}</div>
              <div className="sx-mono text-[9px] tracking-[.1em] uppercase mt-1" style={{ color: c }}>{l}</div>
            </div>
          ))}
        </div>

        {busy && <HitSectionLoading level={level} loading={loading} section="inventory" className="mt-6" />}

        {!busy && drafts.length > 0 && (
          <section className="mt-7 space-y-3">
            <div>
              <div className="sx-mono text-[10px] tracking-[.14em] text-amber-400 flex items-center gap-2"><AlertTriangle size={12} /> BORRADORES PENDIENTES DE PAGO · {drafts.length}</div>
              <p className="text-[13px] text-white/55 mt-1" data-testid="drafts-ttl-notice">Los borradores sin pagar se borran automáticamente a las 48 horas de creados. Paga antes para no perder tu progreso.</p>
            </div>
            {drafts.map((d) => <DraftCard key={d.draft_token} d={d} />)}
          </section>
        )}

        {!busy && auctions.length > 0 && (
          <section className="mt-7">
            <div className="flex gap-2 overflow-x-auto -mx-5 px-5 lg:mx-0 lg:px-0 pb-1 mb-3">
              {FILTERS.map(([k, l]) => (
                <button key={k} type="button" onClick={() => setFilter(k)} data-testid={`mis-filter-${k}`}
                        className={`h-10 px-4 rounded-full text-sm font-bold whitespace-nowrap transition-colors ${filter === k ? "bg-white text-[#080808]" : "border border-white/15 text-white/80 hover:border-white/35"}`}>
                  {l}{k === "all" ? ` ${auctions.length}` : ` ${count(k)}`}
                </button>
              ))}
            </div>
            <div className="space-y-2.5 lg:space-y-0 lg:sx-card lg:rounded-[20px] lg:border lg:border-white/12 lg:overflow-hidden">
              <div className="hidden lg:grid grid-cols-[2.2fr_1fr_1fr_1.2fr_2fr] px-5 py-3 border-b border-white/10 bg-white/[.02]">
                {["Subasta", "Duración", "Salida", "Estado", "Detalle"].map((h) => <Label key={h}>{h}</Label>)}
              </div>
              {shown.map((a) => <AuctionRow key={a.id} a={a} />)}
              {shown.length === 0 && <p className="text-sm text-white/50 p-5">No tienes subastas en este estado.</p>}
            </div>
          </section>
        )}

        {!busy && drafts.length === 0 && auctions.length === 0 && (
          <div className="sx-card p-10 text-center mt-8">
            <Gavel className="mx-auto text-white/25 mb-3" size={32} />
            <p className="text-white/60 mb-5">Todavía no has creado ninguna subasta.</p>
            <Link to="/subastas/crear" className="sx-btn inline-flex h-[52px] px-7 items-center gap-1.5"><Plus size={16} /> Crear mi primera subasta</Link>
          </div>
        )}
        </>}

        <AuctionEmailPrefs />
      </div>
    </div>
  );
}
